import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useWizard } from "../../context/WizardContext";
import Modal from "./Modal";
import ImageEditor from "./ImageEditor";
import { useAuth } from "../../context/AuthContext";
import LogoLoader from "../common/LogoLoader";
import {
  resolveCoverImageUrl,
  resolveStoryImageUrlOriginal,
  appendCacheBust,
} from "../../utils/presignedImageUrl";
import { API_BASE_URL } from "../../config/apiBaseUrl";
import AdminPageNavigator from "../AdminPageNavigator";
import { useTranslation } from "../../i18n/useTranslation";

/** Must match backend `COVER_PROMPT_API_VERSION` when front-cover prompt wording changes. */
const EXPECTED_COVER_PROMPT_API_VERSION = 2;

interface TextOverlay {
  content: string;
  fontSize: number;
  color: string;
  position?: { x: number; y: number };
  alignment?: 'left' | 'center' | 'right';
  fontFamily?: string;
  outlineColor?: string;
  outlineWidth?: number;
}

interface CoverData {
  imageUrl?: string;
  imageStorageKey?: string;
  uploadedImageUrl?: string;
  uploadedImageStorageKey?: string;
  editedImageUrl?: string;
  editedImageStorageKey?: string;
  prompt?: string;
  approved?: boolean;
  textOverlays?: TextOverlay[];
  /** When set by the user for accessibility, used as img alt; otherwise alt stays empty. */
  accessibleDescription?: string;
}

const CoverStudio: React.FC = () => {
  const { t } = useTranslation();
  const { 
    sessionId, 
    language, 
    isLoading,
    error,
    clearError
  } = useWizard();

  const { user, getAuthHeader, token } = useAuth();
  
  const navigate = useNavigate();
  const frontendDevMode = process.env.REACT_APP_PRODUCTION_MODE !== 'true';

  // State management
  const [frontCover, setFrontCover] = useState<CoverData | null>(null);
  const [backCover, setBackCover] = useState<CoverData | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [frontCoverPrompt, setFrontCoverPrompt] = useState<string | null>(null);
  const [backCoverPrompt, setBackCoverPrompt] = useState<string | null>(null);
  const [isDevelopmentMode, setIsDevelopmentMode] = useState<boolean>(frontendDevMode);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isLoadingCover, setIsLoadingCover] = useState<boolean>(true);
  
  // Editing state
  const [editingCover, setEditingCover] = useState<'front' | 'back' | null>(null);
  const [editingOverlayIndex, setEditingOverlayIndex] = useState<number>(-1); // -1 means adding new overlay
  const [isUploadingImage, setIsUploadingImage] = useState<{ front: boolean; back: boolean }>({ front: false, back: false });
  
  const frontFileInputRef = useRef<HTMLInputElement>(null);
  const backFileInputRef = useRef<HTMLInputElement>(null);

  const [coverVisualEpoch, setCoverVisualEpoch] = useState(0);
  const [resolvedFrontUrl, setResolvedFrontUrl] = useState<string | null>(null);
  const [resolvedBackUrl, setResolvedBackUrl] = useState<string | null>(null);
  const [resolvedFrontOriginalUrl, setResolvedFrontOriginalUrl] = useState<string | null>(null);
  const [resolvedBackOriginalUrl, setResolvedBackOriginalUrl] = useState<string | null>(null);
  const [coverPromptBackendWarning, setCoverPromptBackendWarning] = useState<string | null>(null);
  const [expandedCoverPrompt, setExpandedCoverPrompt] = useState<{ front: boolean; back: boolean }>({
    front: false,
    back: false,
  });
  const [isCoverPromptCopied, setIsCoverPromptCopied] = useState<{ front: boolean; back: boolean }>({
    front: false,
    back: false,
  });
  const [loadedStoryTitle, setLoadedStoryTitle] = useState<string>("");

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
    icon?: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  // Load cover data on mount
  useEffect(() => {
    if (!sessionId) {
      navigate("/create/language");
      return;
    }

    if (user?.isAdmin === true) {
      fetch(`${API_BASE_URL}/admin/submissions/${encodeURIComponent(sessionId)}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ adminLastStep: 'cover-studio' }),
      }).catch(() => {});
    }

    const timer = setTimeout(() => {
      loadCoverData();
    }, 100);

    return () => clearTimeout(timer);
  }, [sessionId, navigate, user, getAuthHeader, API_BASE_URL]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await resolveCoverImageUrl(frontCover, token);
      if (!cancelled) setResolvedFrontUrl(u);
    })();
    return () => {
      cancelled = true;
    };
  }, [frontCover, token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await resolveCoverImageUrl(backCover, token);
      if (!cancelled) setResolvedBackUrl(u);
    })();
    return () => {
      cancelled = true;
    };
  }, [backCover, token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await resolveStoryImageUrlOriginal(frontCover, token);
      if (!cancelled) setResolvedFrontOriginalUrl(u);
    })();
    return () => {
      cancelled = true;
    };
  }, [frontCover, token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await resolveStoryImageUrlOriginal(backCover, token);
      if (!cancelled) setResolvedBackOriginalUrl(u);
    })();
    return () => {
      cancelled = true;
    };
  }, [backCover, token]);

  const loadCoverData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}`, {
        headers: { ...getAuthHeader() },
      });
      if (!response.ok) {
        throw new Error('Failed to load story data');
      }
      const data = await response.json();
      const story = data.data;

      const titleFromStory = typeof story.storyTitle === "string" ? story.storyTitle.trim() : "";
      setLoadedStoryTitle(titleFromStory);

      if (!story.generatedStory) {
        setIsLoadingCover(false);
        showModal(
          'error', 
          'No Story Found', 
          'Please generate a story first before creating a cover. You will be redirected to the story studio.',
          () => {
            navigate("/create/story-studio");
          },
          'Go to Story Studio',
          '❌'
        );
        return;
      }

      // Load existing covers
      if (story.frontCover) {
        setFrontCover({
          ...story.frontCover,
          textOverlays: story.frontCover.textOverlays || []
        });
      }
      if (story.backCover) {
        setBackCover({
          ...story.backCover,
          textOverlays: story.backCover.textOverlays || []
        });
      }

      /* Prompt templates are assembled on the server. Always refresh from the API when the app is in
       * front-end dev mode (`REACT_APP_PRODUCTION_MODE` not true), so edits to backend prompts appear
       * without relying on React state flags that can disagree with `PRODUCTION_MODE` on the API. */
      if (frontendDevMode) {
        void generateCovers();
      } else if (!story.frontCover || !story.backCover) {
        void generateCovers();
      }

      setIsLoadingCover(false);
    } catch (err: any) {
      console.error('Failed to load cover data:', err);
      setLocalError(err.message || 'Failed to load cover data');
      setIsLoadingCover(false);
    }
  };

  const generateCovers = async () => {
    setIsGenerating(true);
    clearError();
    setLocalError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/generate-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ coverType: 'both' }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate covers');
      }

      // Check if backend returned development mode
      if (data.data.mode === 'development') {
        setIsDevelopmentMode(true);
        setFrontCoverPrompt(data.data.frontCoverPrompt ?? null);
        setBackCoverPrompt(data.data.backCoverPrompt ?? null);
        const v = data.data._coverPromptVersion;
        if (v !== EXPECTED_COVER_PROMPT_API_VERSION) {
          setCoverPromptBackendWarning(
            v === undefined
              ? 'The API did not send a cover-prompt version header: you may be hitting an older server. Confirm REACT_APP_API_URL (default http://localhost:5000/api) and restart Node from your latest backend folder.'
              : `The API reported cover-prompt version ${String(v)}, but this UI expects ${String(EXPECTED_COVER_PROMPT_API_VERSION)} — connect to an updated backend.`
          );
        } else {
          setCoverPromptBackendWarning(null);
        }
      } else {
        setIsDevelopmentMode(false);
        setCoverPromptBackendWarning(null);
        // Production mode: covers generated
        if (data.data.frontCover) {
          setFrontCover({
            ...data.data.frontCover,
            textOverlays: data.data.frontCover.textOverlays || []
          });
        }
        if (data.data.backCover) {
          setBackCover({
            ...data.data.backCover,
            textOverlays: data.data.backCover.textOverlays || []
          });
        }
      }
    } catch (err: any) {
      console.error('Cover generation failed:', err);
      setLocalError(err.message || 'Failed to generate covers');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReplaceImage = async (coverType: 'front' | 'back') => {
    console.log('handleReplaceImage called with coverType:', coverType);
    
    if (!coverType || (coverType !== 'front' && coverType !== 'back')) {
      console.error('Invalid coverType:', coverType);
      setLocalError('Invalid cover type. Must be "front" or "back"');
      return;
    }

    // Clear the current cover and show prompt/upload interface
    setIsGenerating(true);
    clearError();
    setLocalError(null);

    try {
      // Clear the cover state to remove the image
      if (coverType === 'front') {
        setFrontCover(null);
        setFrontCoverPrompt(null);
      } else {
        setBackCover(null);
        setBackCoverPrompt(null);
      }

      // Generate new cover (this will show prompt in dev mode or generate in production)
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/generate-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ coverType }),
      });

      const data = await response.json();
      console.log('Generate cover response: ok', response.ok, 'mode:', data?.data?.mode);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate cover');
      }

      // Check if backend returned development mode
      if (data.data.mode === 'development') {
        setIsDevelopmentMode(true);
        if (coverType === 'front') {
          setFrontCoverPrompt(data.data.frontCoverPrompt || data.data.prompt);
        } else {
          setBackCoverPrompt(data.data.backCoverPrompt || data.data.prompt);
        }
        const v = data.data._coverPromptVersion;
        if (v !== EXPECTED_COVER_PROMPT_API_VERSION) {
          setCoverPromptBackendWarning(
            v === undefined
              ? 'The API did not send a cover-prompt version header: you may be hitting an older server. Confirm REACT_APP_API_URL and restart Node from your latest backend folder.'
              : `The API reported cover-prompt version ${String(v)}, but this UI expects ${String(EXPECTED_COVER_PROMPT_API_VERSION)} — connect to an updated backend.`
          );
        } else {
          setCoverPromptBackendWarning(null);
        }
      } else {
        setIsDevelopmentMode(false);
        setCoverPromptBackendWarning(null);
        // Production mode: cover generated - reload cover data to get new image
        await loadCoverData();
      }
    } catch (err: any) {
      console.error('Cover replacement failed:', err);
      setLocalError(err.message || 'Failed to replace cover');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async (coverType: 'front' | 'back') => {
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/approve-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ coverType }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to approve cover');
      }

      // Update local state
      if (coverType === 'front') {
        setFrontCover(prev => prev ? { ...prev, approved: true } : null);
      } else {
        setBackCover(prev => prev ? { ...prev, approved: true } : null);
      }

      showModal('success', 'Cover Approved!', `Your ${coverType} cover has been approved successfully.`, undefined, 'Great!', '✅');
    } catch (err: any) {
      console.error('Approval failed:', err);
      setLocalError(err.message || 'Failed to approve cover');
    }
  };

  const showModal = (
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm',
    title: string,
    message: string,
    onConfirm?: () => void,
    confirmText?: string,
    icon?: string
  ) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      confirmText,
      icon
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    closeModal();
  };

  const getImageUrlForEditor = (coverType: 'front' | 'back'): string | null => {
    const cover = coverType === 'front' ? frontCover : backCover;
    const resolved = coverType === 'front' ? resolvedFrontUrl : resolvedBackUrl;
    if (!cover) {
      return null;
    }

    if (cover.editedImageUrl) {
      const url = cover.editedImageUrl.startsWith('blob:') || cover.editedImageUrl.startsWith('http')
        ? cover.editedImageUrl
        : `${API_BASE_URL.replace(/\/api\/?$/, '')}${cover.editedImageUrl}`;
      return appendCacheBust(url, coverVisualEpoch);
    }
    if (cover.editedImageStorageKey) {
      if (resolved) return appendCacheBust(resolved, coverVisualEpoch);
      return null;
    }
    if (cover.uploadedImageUrl) {
      const url = cover.uploadedImageUrl.startsWith('http')
        ? cover.uploadedImageUrl
        : `${API_BASE_URL.replace(/\/api\/?$/, '')}${cover.uploadedImageUrl}`;
      return appendCacheBust(url, coverVisualEpoch);
    }
    if (cover.uploadedImageStorageKey && !cover.uploadedImageUrl) {
      if (resolved) return appendCacheBust(resolved, coverVisualEpoch);
      return null;
    }
    if (cover.imageUrl) {
      const u =
        cover.imageUrl.startsWith('http') || cover.imageUrl.startsWith('blob:')
          ? cover.imageUrl
          : `${API_BASE_URL.replace(/\/api\/?$/, '')}${
              cover.imageUrl.startsWith('/') ? cover.imageUrl : `/${cover.imageUrl}`
            }`;
      return appendCacheBust(u, coverVisualEpoch);
    }
    if (cover.imageStorageKey && !cover.imageUrl) {
      if (resolved) return appendCacheBust(resolved, coverVisualEpoch);
      return null;
    }
    if (resolved) {
      return appendCacheBust(resolved, coverVisualEpoch);
    }
    return null;
  };

  const handleUploadImage = (coverType: 'front' | 'back') => {
    if (coverType === 'front') {
      frontFileInputRef.current?.click();
    } else {
      backFileInputRef.current?.click();
    }
  };

  const handleRemoveAndUploadNew = (coverType: 'front' | 'back') => {
    if (coverType === 'front') {
      setFrontCover((prev) =>
        prev
          ? {
              ...prev,
              imageUrl: undefined,
              imageStorageKey: undefined,
              uploadedImageUrl: undefined,
              uploadedImageStorageKey: undefined,
              editedImageUrl: undefined,
              editedImageStorageKey: undefined,
              textOverlays: [],
            }
          : prev
      );
    } else {
      setBackCover((prev) =>
        prev
          ? {
              ...prev,
              imageUrl: undefined,
              imageStorageKey: undefined,
              uploadedImageUrl: undefined,
              uploadedImageStorageKey: undefined,
              editedImageUrl: undefined,
              editedImageStorageKey: undefined,
              textOverlays: [],
            }
          : prev
      );
    }
    setCoverVisualEpoch((e) => e + 1);
    handleUploadImage(coverType);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, coverType: 'front' | 'back') => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setLocalError('Please select an image file');
      return;
    }

    setIsUploadingImage(prev => ({ ...prev, [coverType]: true }));
    setLocalError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('coverType', coverType);

      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/upload-cover`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload cover image');
      }

      if (coverType === 'front' && data.data.frontCover) {
        setFrontCover({
          ...data.data.frontCover,
          textOverlays: data.data.frontCover.textOverlays || []
        });
        setCoverVisualEpoch((e) => e + 1);
      } else if (coverType === 'back' && data.data.backCover) {
        setBackCover({
          ...data.data.backCover,
          textOverlays: data.data.backCover.textOverlays || []
        });
        setCoverVisualEpoch((e) => e + 1);
      }
    } catch (err: any) {
      console.error('Cover upload failed:', err);
      setLocalError(err.message || 'Failed to upload cover image');
    } finally {
      setIsUploadingImage(prev => ({ ...prev, [coverType]: false }));
      if (coverType === 'front' && frontFileInputRef.current) {
        frontFileInputRef.current.value = '';
      } else if (backFileInputRef.current) {
        backFileInputRef.current.value = '';
      }
    }
  };

  const handleSaveImageEdit = async (coverType: 'front' | 'back', editedImageUrl: string, textOverlay: TextOverlay) => {
    try {
      // Convert blob URL to blob
      const response = await fetch(editedImageUrl);
      const blob = await response.blob();

      const currentCover = coverType === 'front' ? frontCover : backCover;
      const currentOverlays = currentCover?.textOverlays || [];
      
      // Update or add the text overlay
      let updatedOverlays: TextOverlay[];
      if (editingOverlayIndex >= 0 && editingOverlayIndex < currentOverlays.length) {
        // Update existing overlay
        updatedOverlays = [...currentOverlays];
        updatedOverlays[editingOverlayIndex] = textOverlay;
      } else {
        // Add new overlay
        updatedOverlays = [...currentOverlays, textOverlay];
      }

      const formData = new FormData();
      formData.append('editedImage', blob, `cover-${coverType}-edited.png`);
      formData.append('coverType', coverType);
      formData.append('textOverlays', JSON.stringify(updatedOverlays));

      const saveResponse = await fetch(`${API_BASE_URL}/stories/${sessionId}/save-cover-edit`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
        body: formData,
      });

      const data = await saveResponse.json();

      if (!saveResponse.ok) {
        throw new Error(data.message || 'Failed to save edited cover');
      }

      if (coverType === 'front' && data.data.frontCover) {
        setFrontCover({
          ...data.data.frontCover,
          textOverlays: data.data.frontCover.textOverlays || updatedOverlays
        });
        setCoverVisualEpoch((e) => e + 1);
      } else if (coverType === 'back' && data.data.backCover) {
        setBackCover({
          ...data.data.backCover,
          textOverlays: data.data.backCover.textOverlays || updatedOverlays
        });
        setCoverVisualEpoch((e) => e + 1);
      }

      setEditingCover(null);
      setEditingOverlayIndex(-1);

      showModal('success', 'Cover Saved!', `Your edited ${coverType} cover has been saved successfully.`, undefined, 'Great!', '✅');
    } catch (err: any) {
      console.error('Failed to save edited cover:', err);
      setLocalError(err.message || 'Failed to save edited cover');
    }
  };

  const handleAddTextOverlay = (coverType: 'front' | 'back') => {
    setEditingCover(coverType);
    setEditingOverlayIndex(-1); // -1 means new overlay
  };

  const handleEditTextOverlay = (coverType: 'front' | 'back', index: number) => {
    setEditingCover(coverType);
    setEditingOverlayIndex(index);
  };

  const handleDeleteTextOverlay = async (coverType: 'front' | 'back', index: number) => {
    const currentCover = coverType === 'front' ? frontCover : backCover;
    if (!currentCover) return;

    const updatedOverlays = (currentCover.textOverlays || []).filter((_, i) => i !== index);
    
    // If there's an edited image, we need to re-save it with updated overlays
    if (currentCover.editedImageUrl || currentCover.editedImageStorageKey) {
      const imageUrl = getImageUrlForEditor(coverType);
      if (imageUrl) {
        // Re-save with updated overlays (no new image, just update overlays)
        try {
          const formData = new FormData();
          // We need to get the current edited image and re-upload it
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          
          formData.append('editedImage', blob, `cover-${coverType}-edited.png`);
          formData.append('coverType', coverType);
          formData.append('textOverlays', JSON.stringify(updatedOverlays));

          const saveResponse = await fetch(`${API_BASE_URL}/stories/${sessionId}/save-cover-edit`, {
            method: 'POST',
            headers: { ...getAuthHeader() },
            body: formData,
          });

          const data = await saveResponse.json();

          if (saveResponse.ok) {
            if (coverType === 'front' && data.data.frontCover) {
              setFrontCover({
                ...data.data.frontCover,
                textOverlays: data.data.frontCover.textOverlays || updatedOverlays
              });
              setCoverVisualEpoch((e) => e + 1);
            } else if (coverType === 'back' && data.data.backCover) {
              setBackCover({
                ...data.data.backCover,
                textOverlays: data.data.backCover.textOverlays || updatedOverlays
              });
              setCoverVisualEpoch((e) => e + 1);
            }
          }
        } catch (err) {
          console.error('Failed to update overlays:', err);
        }
      }
    } else {
      // Just update local state if no edited image exists
      if (coverType === 'front') {
        setFrontCover({ ...currentCover, textOverlays: updatedOverlays });
      } else {
        setBackCover({ ...currentCover, textOverlays: updatedOverlays });
      }
    }
  };

  const renderCoverSection = (coverType: 'front' | 'back', cover: CoverData | null, prompt: string | null) => {
    const isFront = coverType === 'front';
    const imageUrl = getImageUrlForEditor(coverType);
    const imageStorageKeyForEditor =
      cover?.editedImageStorageKey ||
      cover?.uploadedImageStorageKey ||
      cover?.imageStorageKey ||
      null;
    const hasSavedEditedCover = !!(cover?.editedImageUrl || cover?.editedImageStorageKey);
    const useOriginalForNewOverlay =
      editingCover === coverType && hasSavedEditedCover && editingOverlayIndex < 0;
    const resolvedOriginalUrl = isFront ? resolvedFrontOriginalUrl : resolvedBackOriginalUrl;
    const imageUrlForCanvas = useOriginalForNewOverlay
      ? resolvedOriginalUrl
        ? appendCacheBust(resolvedOriginalUrl, coverVisualEpoch)
        : null
      : imageUrl;
    const storageKeyForCanvas = useOriginalForNewOverlay
      ? cover?.uploadedImageStorageKey || cover?.imageStorageKey || null
      : imageStorageKeyForEditor;
    const isEditing = editingCover === coverType;
    const currentOverlay = editingOverlayIndex >= 0 && cover?.textOverlays 
      ? cover.textOverlays[editingOverlayIndex] 
      : null;

    return (
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            {isFront ? '📖 Front Cover' : '📕 Back Cover'}
          </h2>
          {!isEditing && imageUrl && (
            <button
              onClick={() => handleAddTextOverlay(coverType)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition"
            >
              ➕ Add Text
            </button>
          )}
        </div>
        {prompt && (
          <div className="mb-4">
            <div className="mb-2">
              <button
                type="button"
                onClick={() =>
                  setExpandedCoverPrompt((prev) => ({
                    ...prev,
                    [coverType]: !prev[coverType],
                  }))
                }
                className="w-full rounded-lg border border-blue-200 bg-white px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                aria-expanded={expandedCoverPrompt[coverType]}
                aria-controls={`cover-prompt-panel-${coverType}`}
              >
                <span className="flex items-center justify-between">
                  <span>Complete Prompt</span>
                  <span>{expandedCoverPrompt[coverType] ? '▼' : '▶'}</span>
                </span>
              </button>
            </div>
            <div className="mb-2">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(prompt);
                    setIsCoverPromptCopied((prev) => ({ ...prev, [coverType]: true }));
                    window.setTimeout(
                      () => setIsCoverPromptCopied((prev) => ({ ...prev, [coverType]: false })),
                      2000
                    );
                  } catch {
                    setLocalError('Failed to copy prompt to clipboard.');
                  }
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-base font-semibold hover:bg-blue-700 transition"
              >
                {isCoverPromptCopied[coverType] ? '✅ Copied' : '📋 Copy Prompt'}
              </button>
            </div>
            {expandedCoverPrompt[coverType] && (
              <pre
                id={`cover-prompt-panel-${coverType}`}
                className="whitespace-pre-wrap text-sm text-gray-800 bg-white p-4 rounded border max-h-96 overflow-y-auto"
              >
                {prompt}
              </pre>
            )}
          </div>
        )}

        {isGenerating ? (
          <div className="bg-white p-12 rounded-lg border text-center">
            <LogoLoader size="md" className="mb-4" />
            <p className="text-purple-800 font-medium">
              {isDevelopmentMode ? 'Preparing prompt...' : `Generating ${coverType} cover...`}
            </p>
          </div>
        ) : isEditing && (imageUrlForCanvas || storageKeyForCanvas) ? (
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="mb-4">
              <h3 className="font-semibold text-gray-700 mb-2">
                {editingOverlayIndex >= 0 ? `Editing Text ${editingOverlayIndex + 1}` : 'Adding New Text'}
              </h3>
            </div>
            <ImageEditor
              imageUrl={imageUrlForCanvas}
              imageStorageKey={storageKeyForCanvas}
              pageContent={currentOverlay?.content || ''}
              textOverlay={currentOverlay || undefined}
              textOverlays={cover?.textOverlays}
              editingOverlayIndex={editingOverlayIndex}
              onSave={(editedImageUrl, textOverlay) => handleSaveImageEdit(coverType, editedImageUrl, textOverlay)}
              onSaveMultiple={async (editedImageUrl, textOverlays) => {
                try {
                  // Convert blob URL to blob
                  const response = await fetch(editedImageUrl);
                  const blob = await response.blob();

                  const formData = new FormData();
                  formData.append('editedImage', blob, `cover-${coverType}-edited.png`);
                  formData.append('coverType', coverType);
                  formData.append('textOverlays', JSON.stringify(textOverlays));

                  const saveResponse = await fetch(`${API_BASE_URL}/stories/${sessionId}/save-cover-edit`, {
                    method: 'POST',
                    headers: { ...getAuthHeader() },
                    body: formData,
                  });

                  const data = await saveResponse.json();

                  if (!saveResponse.ok) {
                    throw new Error(data.message || 'Failed to save edited cover');
                  }

                  if (coverType === 'front' && data.data.frontCover) {
                    setFrontCover({
                      ...data.data.frontCover,
                      textOverlays: data.data.frontCover.textOverlays || textOverlays
                    });
                    setCoverVisualEpoch((e) => e + 1);
                  } else if (coverType === 'back' && data.data.backCover) {
                    setBackCover({
                      ...data.data.backCover,
                      textOverlays: data.data.backCover.textOverlays || textOverlays
                    });
                    setCoverVisualEpoch((e) => e + 1);
                  }

                  setEditingCover(null);
                  setEditingOverlayIndex(-1);
                  showModal('success', 'Cover Saved!', `Your edited ${coverType} cover has been saved successfully.`, undefined, 'Great!', '✅');
                } catch (err: any) {
                  console.error('Failed to save edited cover:', err);
                  setLocalError(err.message || 'Failed to save edited cover');
                }
              }}
              onCancel={() => {
                setEditingCover(null);
                setEditingOverlayIndex(-1);
              }}
            />
          </div>
        ) : imageUrl && !isEditing ? (
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="mb-4">
              <img
                src={imageUrl}
                alt={cover?.accessibleDescription?.trim() || ""}
                className="w-full rounded-lg shadow-md"
              />
            </div>
            {cover?.approved && (
              <div className="mb-3 p-2 bg-green-100 text-green-800 rounded text-sm text-center">
                ✅ Approved
              </div>
            )}
            
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => handleAddTextOverlay(coverType)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-lg transition"
              >
                ➕ Add Text Overlay
              </button>
              <button
                onClick={() => handleRemoveAndUploadNew(coverType)}
                disabled={isUploadingImage[coverType]}
                className="px-6 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium text-lg transition"
              >
                Remove image and upload a new one
              </button>
            </div>
          </div>
        ) : isDevelopmentMode && prompt ? (
          <div className="mt-4">
            {imageUrl ? (
              <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
                <img
                  src={imageUrl}
                  alt={cover?.accessibleDescription?.trim() || ""}
                  className="w-full rounded-lg shadow-md"
                />
              </div>
            ) : (
              <p className="text-gray-500 mb-4">No {isFront ? 'front' : 'back'} cover image uploaded yet.</p>
            )}
            <button
              onClick={() => handleUploadImage(coverType)}
              disabled={isUploadingImage[coverType]}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-lg"
            >
              {isUploadingImage[coverType] ? 'Uploading...' : `📤 Upload ${isFront ? 'Front' : 'Back'} Cover Image`}
            </button>
          </div>
        ) : (
          <div className="bg-white p-12 rounded-lg border text-center">
            {isDevelopmentMode ? (
              <div className="space-y-4">
                <p className="text-gray-500 mb-4">No {isFront ? 'front' : 'back'} cover image uploaded yet.</p>
                <button
                  onClick={() => handleUploadImage(coverType)}
                  disabled={isUploadingImage[coverType]}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold px-8 py-4 rounded-xl text-lg shadow-md transition transform hover:scale-105"
                >
                  {isUploadingImage[coverType] ? 'Uploading...' : `📤 Upload ${isFront ? 'Front' : 'Back'} Cover`}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-500">No {isFront ? 'front' : 'back'} cover generated yet.</p>
                <button
                  onClick={() => generateCovers()}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition"
                >
                  🎨 Generate {isFront ? 'Front' : 'Back'} Cover
                </button>
              </div>
            )}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={isFront ? frontFileInputRef : backFileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileChange(e, coverType)}
          className="hidden"
        />
      </div>
    );
  };

  return (
    <div 
      className="relative w-full min-h-screen bg-cover bg-center bg-no-repeat flex flex-col items-center px-4 pt-8"
      style={{
        backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/wizard/3_background.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <main
        id="main-content"
        tabIndex={-1}
        className="relative flex w-full max-w-6xl flex-1 flex-col items-center outline-none"
      >
      {/* Error Display */}
      {coverPromptBackendWarning && (
        <div className="bg-amber-50 border border-amber-300 text-amber-950 px-4 py-3 rounded mb-4 max-w-4xl w-full text-sm leading-relaxed">
          <span className="block sm:inline">{coverPromptBackendWarning}</span>
          <button
            type="button"
            onClick={() => setCoverPromptBackendWarning(null)}
            className="float-right ml-2 text-amber-900 hover:text-amber-950 font-semibold"
          >
            ×
          </button>
        </div>
      )}
      {(error || localError) && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 max-w-4xl w-full">
          <span className="block sm:inline">{error || localError}</span>
          <button
            onClick={() => {
              clearError();
              setLocalError(null);
            }}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 pb-24 shadow-card border border-white/40 max-w-6xl w-full z-10">
        <h1 className="text-3xl font-extrabold text-heading mb-3 text-center">
          Cover Generation Studio 📖
        </h1>
        <AdminPageNavigator align="center" />

        {/* Loading State */}
        {isLoadingCover ? (
          <div className="mb-6 p-6 bg-blue-50 rounded-lg text-center">
            <LogoLoader size="md" className="mb-4" />
            <p className="text-blue-800 font-medium">Loading cover data...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {loadedStoryTitle ? (
              <div className="rounded-xl border border-brandBeige/80 bg-brandBeige/25 px-4 py-3 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {t("wizard.coverStudio.submittedStoryName")}
                </p>
                <p className="mt-1 text-xl font-extrabold text-heading break-words">{loadedStoryTitle}</p>
              </div>
            ) : null}
            {/* Front Cover Section */}
            {renderCoverSection('front', frontCover, frontCoverPrompt)}

            {/* Back Cover Section */}
            {renderCoverSection('back', backCover, backCoverPrompt)}

            {/* Generate Both Covers Button (Production Mode) */}
            {!isDevelopmentMode && (!frontCover || !backCover) && !isGenerating && (
              <div className="text-center">
                <button
                  onClick={generateCovers}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-4 rounded-xl text-lg shadow-md transition transform hover:scale-105"
                >
                  🎨 Generate Both Covers
                </button>
              </div>
            )}

          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={() => navigate("/create/image-studio")}
            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105"
          >
            ← Back to Images
          </button>

          <div className="flex gap-3">
            {/* View Book button - show if at least one cover has been saved */}
            {(getImageUrlForEditor('front') || getImageUrlForEditor('back')) && (
              <button
                onClick={() => navigate("/create/result")}
                disabled={editingCover !== null}
                className={`bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                📖 View Book
              </button>
            )}

            {/* Finish button - show only if both covers are approved */}
            {frontCover?.approved && backCover?.approved && (
              <button
                onClick={() => navigate("/create/result")}
                disabled={editingCover !== null}
                className={`bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Finish →
              </button>
            )}
          </div>
        </div>
      </div>
      </main>

      {/* Custom Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onConfirm={modalState.onConfirm ? handleModalConfirm : undefined}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        icon={modalState.icon}
      />
    </div>
  );
};

export default CoverStudio;


